// Card payment in progress. The headline is the live IM30 status: in
// Spanish we show the terminal's own message; in English we map known status
// codes (card / PIN / authorizing / installments) and fall back to the
// terminal text. Discreet step dots stand in for the raw IM30 code so the
// visitor never sees "Estado IM30: 81"; the code→dot mapping lives in
// card-progress.ts.

import { ContactlessCard } from "../components/Illustrations";
import { KioskShell } from "../components/KioskShell";
import { ProgressBar } from "../components/ProgressBar";
import { type Translate, useLang } from "../i18n/LangContext";
import { STRINGS, type StringKey } from "../i18n/strings";
import type { KioskState } from "../state/types";
import { activeDotIndex } from "./card-progress";

type CardProcessing = Extract<KioskState, { phase: "card-processing" }>;

const DOT_KEYS: StringKey[] = ["dotCard", "dotPin", "dotAuth", "dotApproved"];

function dotModifier(index: number, active: number): string {
  if (index < active) {
    return "status-dot--done";
  }
  if (index === active) {
    return "status-dot--active";
  }
  return "";
}

function headlineFor(
  lang: "es" | "en",
  code: string,
  terminalMessage: string,
  t: Translate
): string {
  if (lang === "en") {
    const key = `im30.${code}`;
    if (Object.hasOwn(STRINGS.en, key)) {
      return t(key as StringKey);
    }
  }
  return terminalMessage;
}

export function CardProcessingScreen({ state }: { state: CardProcessing }) {
  const { t, lang } = useLang();
  const active = activeDotIndex(state.statusCode);
  const headline =
    headlineFor(lang, state.statusCode, state.statusMessage, t) ||
    t("cardProcessing");

  return (
    <KioskShell step={3}>
      <div className="screen screen--center">
        <div className="illustration">
          <ContactlessCard active />
        </div>
        <h1 className="screen__title">{headline}</h1>
        <div className="status-dots">
          {DOT_KEYS.map((key, i) => (
            <span
              className={["status-dot", dotModifier(i, active)]
                .filter(Boolean)
                .join(" ")}
              key={key}
            >
              <span className="status-dot__mark" />
              <span>{t(key)}</span>
            </span>
          ))}
        </div>
        <ProgressBar />
      </div>
    </KioskShell>
  );
}
